import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../../utils/cn';

const GRID_LINES = 8;
const NODES = [
  { x: 18, y: 22 },
  { x: 72, y: 35 },
  { x: 45, y: 58 },
  { x: 82, y: 68 },
  { x: 28, y: 78 },
];

interface AuthAnimatedBackgroundProps {
  variant?: 'light' | 'dark';
}

export const AuthAnimatedBackground: React.FC<AuthAnimatedBackgroundProps> = ({
  variant = 'light',
}) => {
  const reduceMotion = useReducedMotion();
  const isDark = variant === 'dark';

  return (
    <motion.div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
      initial={false}
    >
      <motion.div
        className={cn('absolute inset-0', isDark ? 'opacity-50' : 'opacity-[0.32]')}
        style={{
          backgroundImage: isDark
            ? `linear-gradient(to right, rgb(255 255 255 / 0.04) 1px, transparent 1px),
               linear-gradient(to bottom, rgb(255 255 255 / 0.04) 1px, transparent 1px)`
            : `linear-gradient(to right, rgb(226 232 240 / 0.45) 1px, transparent 1px),
               linear-gradient(to bottom, rgb(226 232 240 / 0.45) 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
        }}
        animate={reduceMotion ? {} : { backgroundPosition: ['0px 0px', '48px 48px'] }}
        transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
      />

      <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="authLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0" />
            <stop offset="50%" stopColor="#2563eb" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="authPulseGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0d9488" stopOpacity="0" />
            <stop offset="50%" stopColor="#0d9488" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.22, 0.52, 0.76].map((yRatio, i) => (
          <motion.path
            key={`curve-${i}`}
            d={`M0 ${100 * yRatio} Q 200 ${100 * (yRatio - 0.06)} 400 ${100 * yRatio} T 800 ${100 * yRatio}`}
            fill="none"
            stroke="url(#authLineGrad)"
            strokeWidth="1.25"
            vectorEffect="non-scaling-stroke"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={
              reduceMotion
                ? { pathLength: 1, opacity: 0.22 }
                : {
                    pathLength: 1,
                    opacity: [0.12, 0.28, 0.12],
                  }
            }
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    pathLength: { duration: 2.4, delay: i * 0.35, ease: 'easeOut' },
                    opacity: { duration: 7, repeat: Infinity, ease: 'easeInOut', delay: i * 0.9 },
                  }
            }
            style={{ transform: 'scaleX(1.35) translateX(-8%)' }}
          />
        ))}

        <motion.path
          d="M0 62 Q 320 48 640 58 T 960 55"
          fill="none"
          stroke="url(#authPulseGrad)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          animate={reduceMotion ? {} : { opacity: [0.08, 0.22, 0.08] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </svg>

      {!reduceMotion &&
        NODES.map((node, i) => (
          <motion.div
            key={`node-${i}`}
            className={cn(
              'absolute h-1.5 w-1.5 rounded-full',
              isDark ? 'bg-blue-400/50' : 'bg-blue-500/35'
            )}
            style={{ left: `${node.x}%`, top: `${node.y}%` }}
            animate={{ opacity: [0.25, 0.65, 0.25], scale: [1, 1.15, 1] }}
            transition={{
              duration: 3.2 + i * 0.4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.5,
            }}
          />
        ))}

      {!reduceMotion &&
        Array.from({ length: GRID_LINES }).map((_, i) => (
          <motion.div
            key={`scan-${i}`}
            className={cn(
              'absolute h-px w-20 bg-gradient-to-r from-transparent to-transparent',
              isDark ? 'via-blue-400/30' : 'via-blue-500/25'
            )}
            style={{
              left: `${6 + (i * 11) % 82}%`,
              top: `${10 + (i * 13) % 78}%`,
            }}
            animate={{ opacity: [0.05, 0.35, 0.05], x: [0, 16, 0] }}
            transition={{
              duration: 5 + (i % 2),
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.35,
            }}
          />
        ))}
    </motion.div>
  );
};
